import {
  BaseQueryApi,
  QueryReturnValue,
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { paginatedQuery } from 'lib-shared-api-utils';
import {
  EndpointExtraOptions,
  EndpointOptions,
  QueryError,
} from 'lib-shared-auth';
import type {
  CreateSavedSearchApiArg,
  CreateSavedSearchApiResponse,
  DeleteSavedSearchApiArg,
  DeleteSavedSearchApiResponse,
  ReadAdvancedSearchConversationsIdsBulkApiArg,
  ReadAdvancedSearchConversationsIdsBulkApiResponse,
  ReadAdvancedSearchResultsApiArg,
  ReadAdvancedSearchResultsApiResponse,
  ReadSavedSearchApiArg,
  ReadSavedSearchApiResponse,
  ReadSavedSearchListApiResponse,
  ReadSearchApiArg,
  ReadSearchApiResponse,
  ReadSearchCompanyApiArg,
  ReadSearchCompanyApiResponse,
  ReadSearchConversationApiArg,
  ReadSearchConversationApiResponse,
  ReadSearchEnrichedFiltersApiArg,
  ReadSearchEnrichedFiltersApiResponse,
  ReadSearchMessageApiArg,
  ReadSearchMessageApiResponse,
  ReadSearchQueueApiArg,
  ReadSearchQueueApiResponse,
  ReadSearchTagApiArg,
  ReadSearchTagApiResponse,
  ReadSearchUserApiArg,
  ReadSearchUserApiResponse,
  SearchScope,
  UpdateSavedSearchApiArg,
  UpdateSavedSearchApiResponse,
} from 'lib-shared-types-api';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import {
  createReadSearchResultsSearchParams,
  transformReadAdvancedSearchResultsArg,
} from './utils/transformArg';
import {
  transformCreateSavedSearchResponse,
  transformReadAdvancedSearchResultsResponse,
  transformReadSavedSearchListResponse,
  transformReadSavedSearchResponse,
  transformReadSearchEnrichedFiltersResponse,
  transformReadSearchResultsResponse,
  transformUpdateSavedSearchResponse,
} from './utils/transformResponse';

export const searchApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readSearchConversation: builder.query<
      ReadSearchConversationApiResponse,
      ReadSearchConversationApiArg
    >(
      getReadSearchEndpointDefinition<
        ReadSearchConversationApiResponse,
        ReadSearchConversationApiArg
      >('conversations'),
    ),
    readSearchMessage: builder.query<
      ReadSearchMessageApiResponse,
      ReadSearchMessageApiArg
    >(
      getReadSearchEndpointDefinition<
        ReadSearchMessageApiResponse,
        ReadSearchMessageApiArg
      >('message'),
    ),
    readSearchQueue: builder.query<
      ReadSearchQueueApiResponse,
      ReadSearchQueueApiArg
    >(
      getReadSearchEndpointDefinition<
        ReadSearchQueueApiResponse,
        ReadSearchQueueApiArg
      >('queue'),
    ),
    readSearchTag: builder.query<ReadSearchTagApiResponse, ReadSearchTagApiArg>(
      getReadSearchEndpointDefinition<
        ReadSearchTagApiResponse,
        ReadSearchTagApiArg
      >('tag'),
    ),
    readSearchCompany: builder.query<
      ReadSearchCompanyApiResponse,
      ReadSearchCompanyApiArg
    >(
      getReadSearchEndpointDefinition<
        ReadSearchCompanyApiResponse,
        ReadSearchCompanyApiArg
      >('company'),
    ),
    readSearchUser: builder.query<
      ReadSearchUserApiResponse,
      ReadSearchUserApiArg
    >(
      getReadSearchEndpointDefinition<
        ReadSearchUserApiResponse,
        ReadSearchUserApiArg
      >('user'),
    ),
    readAdvancedSearchResults: builder.query<
      ReadAdvancedSearchResultsApiResponse,
      ReadAdvancedSearchResultsApiArg
    >({
      query: (arg) => {
        const {
          params: { scope, ...body },
        } = transformReadAdvancedSearchResultsArg(arg);

        return {
          version: 3,
          path: `search/advanced/${scope}`,
          method: 'POST',
          body,
        };
      },
      transformResponse: transformReadAdvancedSearchResultsResponse,
    }),
    readAdvancedSearchConversationsIdsBulk: builder.query<
      ReadAdvancedSearchConversationsIdsBulkApiResponse,
      ReadAdvancedSearchConversationsIdsBulkApiArg
    >({
      query: (arg) => {
        const { params: body } = transformReadAdvancedSearchResultsArg(arg);

        return {
          version: 3,
          path: 'search/advanced/bulk/ids',
          method: 'POST',
          body,
        };
      },
    }),
    readSavedSearchList: builder.query<ReadSavedSearchListApiResponse, void>({
      query: () => ({
        version: 3,
        path: 'search/saved',
      }),
      transformResponse: transformReadSavedSearchListResponse,
    }),
    readSavedSearch: builder.query<
      ReadSavedSearchApiResponse,
      ReadSavedSearchApiArg
    >({
      query: ({ id }) => ({
        version: 3,
        path: `search/saved/${id}`,
      }),
      transformResponse: transformReadSavedSearchResponse,
    }),
    createSavedSearch: builder.mutation<
      CreateSavedSearchApiResponse,
      CreateSavedSearchApiArg
    >({
      query: (body) => ({
        version: 3,
        path: 'search/saved',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const {
            data: {
              data: { saved_search },
            },
          } = await queryFulfilled;

          dispatch(
            searchApi.util.updateQueryData(
              'readSavedSearchList',
              undefined,
              (draft) => {
                draft.data.saved_searches.push(saved_search);
              },
            ),
          );
        } catch {}
      },
      transformResponse: transformCreateSavedSearchResponse,
    }),
    updateSavedSearch: builder.mutation<
      UpdateSavedSearchApiResponse,
      UpdateSavedSearchApiArg
    >({
      query: (arg) => ({
        version: 3,
        path: `search/saved/${arg.saved_search.id}`,
        method: 'PATCH',
        body: arg.saved_search,
      }),
      async onQueryStarted({ saved_search }, { dispatch, queryFulfilled }) {
        const patchReadSavedSearchListQuery = dispatch(
          searchApi.util.updateQueryData(
            'readSavedSearchList',
            undefined,
            (draft) => {
              const savedSearchList = draft.data.saved_searches as DeepWritable<
                typeof draft.data.saved_searches
              >;

              const index = draft.data.saved_searches.findIndex(
                (savedSearch) => savedSearch.id === saved_search.id,
              );

              if (index !== -1) {
                savedSearchList[index] = {
                  ...savedSearchList[index],
                  ...saved_search,
                };
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadSavedSearchListQuery.undo();
        }
      },
      transformResponse: transformUpdateSavedSearchResponse,
    }),
    deleteSavedSearch: builder.mutation<
      DeleteSavedSearchApiResponse,
      DeleteSavedSearchApiArg
    >({
      query: ({ id }) => ({
        version: 3,
        path: `search/saved/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchReadSavedSearchListQuery = dispatch(
          searchApi.util.updateQueryData(
            'readSavedSearchList',
            undefined,
            (draft) => {
              const savedSearchList = draft.data.saved_searches as DeepWritable<
                typeof draft.data.saved_searches
              >;

              const index = draft.data.saved_searches.findIndex(
                (savedSearch) => savedSearch.id === id,
              );

              if (index !== -1) {
                savedSearchList.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadSavedSearchListQuery.undo();
        }
      },
    }),
    readSearchEnrichedFilters: builder.query<
      ReadSearchEnrichedFiltersApiResponse,
      ReadSearchEnrichedFiltersApiArg
    >({
      query: ({ filters }) => {
        return {
          version: 3,
          path: 'search/saved/validate',
          method: 'POST',
          body: {
            params: {
              filters,
            },
          },
        };
      },
      transformResponse: transformReadSearchEnrichedFiltersResponse,
    }),
  }),
});

/**
 * Gets the endpoint definition for the read search endpoint.
 *
 * @param type The search type.
 */
function getReadSearchEndpointDefinition<
  TReadSearchApiResponse extends ReadSearchApiResponse,
  TReadSearchApiArg extends ReadSearchApiArg,
>(type: SearchScope) {
  return {
    async queryFn(
      arg: TReadSearchApiArg,
      _api: BaseQueryApi,
      _extraOptions: EndpointExtraOptions,
      baseQuery: (
        arg: EndpointOptions | string,
      ) => MaybePromise<
        QueryReturnValue<unknown, QueryError, Record<string, unknown>>
      >,
    ) {
      const response = await baseQuery(
        paginatedQuery(
          arg,
          (arg) => ({
            version: 2,
            path: 'search',
            params: createReadSearchResultsSearchParams(type, arg),
          }),
          {
            sort: {
              paramName: 'sortBy',
            },
            search: {
              paramName: 'query',
              default: '',
              supportsSortedSearch: true,
            },
            pagination: {
              paramNaming: 'pageSizePage',
              defaultLimit: 50,
            },
          },
        ),
      );

      return {
        data: transformReadSearchResultsResponse<TReadSearchApiResponse>(
          response.data,
          arg,
          type,
        ),
      };
    },
  };
}
